import type React from 'react';

import * as styles from './styles';
import { SidebarFooter } from './SidebarFooter/SidebarFooter';
import { SidebarGroup } from './SidebarGroup/SidebarGroup';
import { SidebarItem } from './SidebarItem/SidebarItem';
import { SidebarItemDropdown } from './SidebarItemDropdown/SidebarItemDropdown';
import { SidebarItemDropdownEntry } from './SidebarItemDropdownEntry/SidebarItemDropdownEntry';
import SidebarMediaRequestItem from './SidebarMediaRequestItem';
import SidebarOnboarding from './SidebarOnboarding';
import { SidebarUser } from './SidebarUser/SidebarUser';

import {
  type AccessibleFleet,
  Location,
  locationName,
  type Session,
} from '@cobliteam/common-types';

import type { WebData } from '@cobliteam/api-lib';

import { RoutingPermissions } from '@cobliteam/routing/src/permissions';

import { useLocalStorage, usePermissions, useUser } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';

import {
  AnalysisIcon,
  ChevronIcon,
  DrawerIcon,
  FactCheckCb,
  IntegrationsIcon,
  MenuItem,
  MenuItemType,
  RuleIconCb,
  SafetyIconCb,
  TripsIcon,
  VehicleGroupIcon,
} from '@cobliteam/base-components';
import {
  badgeColors,
  BadgeSidebar,
  type BadgeSidebarProps,
  BadgeSidebarType,
} from '@cobliteam/base-components/src/BadgeSidebar/Badge';

import {
  HomeRounded,
  MapRounded,
  MonetizationOnRounded,
  NotificationsRounded,
  SwapCallsRounded,
  Videocam,
} from '@material-ui/icons';

export interface SidebarProps {
  open: boolean;
  userEmail: string;
  currentLocation: Location;
  loginWebData: WebData<Session>;
  onToggleClick: () => void;
  onSidebarItemClick?: (location: Location) => void;
  changeUserFleet: (fleet: AccessibleFleet) => void;
  logout: () => void;
  accessibleFleetsWebData: WebData<AccessibleFleet[]>;
}

interface BadgeExpirationProps {
  expirationDate?: Date;
  localStorageKeyToExpireOnClick?: string;
}

const then = new Date();
then.setMonth(then.getMonth() - 1);

export const Sidebar: React.FC<SidebarProps> = ({
  currentLocation,
  loginWebData,
  ...props
}) => {
  const { useGetUserData } = useUser();
  const { data: userData } = useGetUserData();
  const { isAccessAllowed } = usePermissions();
  const hasCreateUpdateDeleteRoutesPermission = isAccessAllowed(
    RoutingPermissions.CREATE_UPDATE_DELETE_ROUTES
  );

  const {
    showFleetPolicyPage,
    showMulticamLandingPage,
    showAdvancedAnalyticsPage,
    integrationsPage: showIntegrationsPage,
  } = useFeatureFlags();

  const useBadgeExpiration = ({
    expirationDate,
    localStorageKeyToExpireOnClick,
  }: BadgeExpirationProps) => {
    const localStorageExpirationKey = `hasViewed${localStorageKeyToExpireOnClick}`;
    const [hasViewedNewFeature, setHasViewedNewFeature] =
      useLocalStorage<boolean>(localStorageExpirationKey);

    const isExpiredByClick =
      !!localStorageKeyToExpireOnClick && hasViewedNewFeature;
    const isExpiredByDate = !!expirationDate && expirationDate <= new Date();

    return {
      isExpired: isExpiredByClick || isExpiredByDate,
      setHasViewedNewFeature,
    };
  };

  const renderSidebarItem = (
    location: Location,
    icon: React.ReactElement<SVGAElement>,
    customOptions?: {
      badgeType?: BadgeSidebarType;
      title?: string;
      featureFlag?: boolean;
    }
  ) => {
    const { badgeType, title, featureFlag } = customOptions || {};

    if (featureFlag === false) return null;

    return (
      <SidebarItem
        location={location}
        sidebarOpen={props.open}
        active={currentLocation.includes(location)}
        icon={icon}
        badge={badgeType && <BadgeSidebar type={badgeType} />}
        dataTest={`sidebar-item-${locationName[location]
          .toLowerCase()
          .replace(/ /g, '-')}`}
      >
        {title || locationName[location]}
      </SidebarItem>
    );
  };

  const renderSidebarDropdownItem = (
    location: Location,
    badge?: BadgeSidebarProps,
    onClick?: () => void,
    featureFlag?: boolean,
    activeLocations?: Location[],
    title?: string
  ) => {
    if (featureFlag === false) return null;
    return (
      <SidebarItemDropdownEntry
        location={location}
        active={
          activeLocations
            ? activeLocations.includes(currentLocation)
            : currentLocation === location
        }
        dataTest={`sidebar-item-${locationName[location]
          .toLowerCase()
          .replace(/ /g, '-')}`}
        onClick={onClick}
      >
        <>
          {title || locationName[location]}
          {badge && !badge.expired && (
            <div className={styles.badgeCircle(badgeColors[badge.type])} />
          )}
        </>
      </SidebarItemDropdownEntry>
    );
  };

  const renderMultiCamPage = () => {
    if (showMulticamLandingPage) {
      return renderSidebarItem(Location.MULTICAM_LANDING_PAGE, <Videocam />, {
        badgeType: !userData?.hasCameraFlag ? BadgeSidebarType.NEW : undefined,
      });
    }

    return renderSidebarItem(Location.CAMERA, <Videocam />);
  };

  const reportPathsWithParams = [
    Location.REPORTS,
    Location.REPORTS_NEW,
    Location.REPORTS_TRIP_AND_STOP,
    Location.REPORTS_TRIP_AND_STOP_TYPE,
    Location.REPORTS_TRIP_AND_STOP_DETAILS,
    Location.REPORTS_TRIP_AND_STOP_DAILY,
    Location.REPORTS_FLEET_SAFETY_TYPE,
  ].flatMap((location) => {
    if (location.includes(':type')) {
      return [
        location.replace(':type', 'driver'),
        location.replace(':type', 'vehicle'),
      ] as Location[];
    } else return [location];
  });

  const renderSidebarEntries = () => (
    <div role='tabsEntries' className={styles.sidebarScroll(props.open)}>
      <SidebarGroup>
        {renderSidebarItem(Location.HOME, <HomeRounded />)}
      </SidebarGroup>

      <SidebarGroup>
        {renderSidebarItem(Location.MONITORING, <MapRounded />)}
        {renderSidebarItem(Location.TRACKING_HISTORY, <TripsIcon />)}

        {renderSidebarItem(Location.ALERTS, <NotificationsRounded />)}

        {showFleetPolicyPage && (
          <SidebarItemDropdown
            title='Política de Frota'
            icon={<FactCheckCb />}
            active={[
              Location.FLEET_POLICY,
              Location.FLEET_POLICY_GEOFENCE,
              Location.FLEET_POLICY_IDLE_ENGINE,
              Location.FLEET_POLICY_DEVICE_CONNECTION,
              Location.TRIGGERS,
              Location.TRIGGERS_CREATE,
              Location.TRIGGERS_EDIT,
            ].includes(currentLocation)}
            sidebarOpen={props.open}
            groupedLocations={[
              Location.FLEET_POLICY,
              Location.FLEET_POLICY_GEOFENCE,
              Location.FLEET_POLICY_IDLE_ENGINE,
              Location.FLEET_POLICY_DEVICE_CONNECTION,
              Location.TRIGGERS,
              Location.TRIGGERS_CREATE,
              Location.TRIGGERS_EDIT,
            ]}
            badge={<BadgeSidebar type={BadgeSidebarType.NEW} />}
          >
            {renderSidebarDropdownItem(
              Location.FLEET_POLICY,
              undefined,
              undefined,
              undefined,
              undefined,
              'Página inicial'
            )}
            {renderSidebarDropdownItem(
              Location.TRIGGERS,
              undefined,
              undefined,
              undefined,
              undefined,
              'Minhas regras'
            )}
            {renderSidebarDropdownItem(
              Location.TRIGGERS_CREATE,
              undefined,
              undefined,
              undefined,
              undefined,
              'Criar regra'
            )}
          </SidebarItemDropdown>
        )}

        {userData?.hasCameraFlag ? (
          <SidebarItemDropdown
            title='Cobli Cam'
            icon={<Videocam />}
            active={[
              Location.CAMERA_EVENT_ALL_EVENTS,
              Location.CAMERA_EVENT_FAVORITE,
              Location.CAMERA_REQUEST,
              Location.CAMERA_REQUEST_ALL_REQUESTS,
              Location.CAMERA_REQUEST_FAVORITE,
              Location.CAMERA_TRIP_IMAGE_HISTORY,
              Location.CAMERA_TRIP_IMAGE_HISTORY_ALL_TRIPS,
              Location.CAMERA_TRIP_IMAGE_HISTORY_PENDING_TRIPS,
            ].includes(currentLocation)}
            sidebarOpen={props.open}
            groupedLocations={[
              Location.CAMERA_EVENT_ALL_EVENTS,
              Location.CAMERA_EVENT_FAVORITE,
              Location.CAMERA_REQUEST,
              Location.CAMERA_REQUEST_ALL_REQUESTS,
              Location.CAMERA_REQUEST_FAVORITE,
              Location.CAMERA_TRIP_IMAGE_HISTORY,
              Location.CAMERA_TRIP_IMAGE_HISTORY_PENDING_TRIPS,
              Location.CAMERA_TRIP_IMAGE_HISTORY_ALL_TRIPS,
            ]}
          >
            {renderSidebarDropdownItem(
              Location.CAMERA_EVENT_ALL_EVENTS,
              undefined,
              undefined,
              undefined,
              [Location.CAMERA_EVENT_ALL_EVENTS, Location.CAMERA_EVENT_FAVORITE]
            )}
            <SidebarMediaRequestItem currentLocation={currentLocation} />
            {renderSidebarDropdownItem(
              Location.CAMERA_TRIP_IMAGE_HISTORY_PENDING_TRIPS,
              undefined,
              undefined,
              undefined,
              [
                Location.CAMERA_TRIP_IMAGE_HISTORY_ALL_TRIPS,
                Location.CAMERA_TRIP_IMAGE_HISTORY_PENDING_TRIPS,
              ]
            )}
          </SidebarItemDropdown>
        ) : (
          renderMultiCamPage()
        )}
      </SidebarGroup>

      <SidebarGroup>
        <>
          {hasCreateUpdateDeleteRoutesPermission ? (
            <SidebarItemDropdown
              title='Serviços e Entregas'
              icon={<SwapCallsRounded />}
              active={[
                Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER,
                Location.SERVICES_AND_DELIVERIES_PLANNING,
              ].includes(currentLocation)}
              sidebarOpen={props.open}
              groupedLocations={[
                Location.SERVICES_AND_DELIVERIES_PLANNING,
                Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER,
              ]}
              dataCy='services-and-deliveries-sidebar-dropdown'
            >
              {renderSidebarDropdownItem(
                Location.SERVICES_AND_DELIVERIES_PLANNING
              )}
              {renderSidebarDropdownItem(
                Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER
              )}
            </SidebarItemDropdown>
          ) : (
            renderSidebarItem(
              Location.SERVICES_AND_DELIVERIES_CONTROL_TOWER,
              <SwapCallsRounded />,
              {
                title: 'Serviços e Entregas',
              }
            )
          )}
        </>
        <SidebarItemDropdown
          title='Documentos'
          icon={<RuleIconCb />}
          active={[Location.PROOF_OF_CONCLUSION, Location.CHECKLIST].includes(
            currentLocation
          )}
          groupedLocations={[Location.PROOF_OF_CONCLUSION, Location.CHECKLIST]}
          sidebarOpen={props.open}
        >
          {renderSidebarDropdownItem(Location.PROOF_OF_CONCLUSION)}
          {renderSidebarDropdownItem(Location.CHECKLIST)}
        </SidebarItemDropdown>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarItemDropdown
          title='Segurança'
          icon={<SafetyIconCb />}
          active={[
            Location.SPEEDING,
            Location.DRIVER_BEHAVIOR,
            Location.INSURANCE,
          ].includes(currentLocation)}
          sidebarOpen={props.open}
          dataTest='sidebar-item-seguranca'
          groupedLocations={[
            Location.SPEEDING,
            Location.DRIVER_BEHAVIOR,
            Location.INSURANCE,
          ]}
        >
          {renderSidebarDropdownItem(Location.SPEEDING)}
          {renderSidebarDropdownItem(Location.DRIVER_BEHAVIOR)}
        </SidebarItemDropdown>
        <SidebarItemDropdown
          title='Análises'
          icon={<AnalysisIcon />}
          badge={<BadgeSidebar type={BadgeSidebarType.NEW}></BadgeSidebar>}
          active={[
            Location.PRODUCTIVITY,
            Location.ADVANCED_ANALYTICS,
            ...reportPathsWithParams,
          ].includes(currentLocation)}
          sidebarOpen={props.open}
          groupedLocations={[
            Location.PRODUCTIVITY,
            Location.REPORTS,
            Location.ADVANCED_ANALYTICS,
          ]}
        >
          {renderSidebarDropdownItem(Location.PRODUCTIVITY)}
          {renderSidebarDropdownItem(
            Location.REPORTS,
            { type: BadgeSidebarType.NEW },
            undefined,
            undefined,
            reportPathsWithParams
          )}
          {renderSidebarDropdownItem(
            Location.ADVANCED_ANALYTICS,
            undefined,
            undefined,
            showAdvancedAnalyticsPage
          )}
        </SidebarItemDropdown>

        <SidebarItemDropdown
          title='Gastos'
          icon={<MonetizationOnRounded />}
          active={[
            Location.FUEL_CARD,
            Location.MAINTENANCE,
            Location.COSTS,
          ].includes(currentLocation)}
          groupedLocations={[
            Location.FUEL_CARD,
            Location.MAINTENANCE,
            Location.COSTS,
          ]}
          sidebarOpen={props.open}
        >
          {renderSidebarDropdownItem(Location.FUEL_CARD)}
          {renderSidebarDropdownItem(Location.MAINTENANCE)}
          {renderSidebarDropdownItem(Location.COSTS)}
        </SidebarItemDropdown>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarItemDropdown
          title='Minha frota'
          icon={<VehicleGroupIcon />}
          active={[
            Location.VEHICLES,
            Location.DEVICES,
            Location.DRIVER_MANAGEMENT,
            Location.VEHICLES_GROUPS,
          ].includes(currentLocation)}
          sidebarOpen={props.open}
          groupedLocations={[
            Location.VEHICLES,
            Location.DEVICES,
            Location.DRIVER_MANAGEMENT,
            Location.VEHICLES_GROUPS,
          ]}
        >
          {renderSidebarDropdownItem(Location.VEHICLES)}
          {renderSidebarDropdownItem(Location.VEHICLES_GROUPS)}
          {renderSidebarDropdownItem(Location.DEVICES)}
          {renderSidebarDropdownItem(Location.DRIVER_MANAGEMENT)}
        </SidebarItemDropdown>
        {showIntegrationsPage && (
          <SidebarItemDropdown
            title='Integrações'
            icon={<IntegrationsIcon />}
            active={[Location.APPS, Location.APIS, Location.WEBHOOKS].includes(
              currentLocation
            )}
            sidebarOpen={props.open}
            dataCy='routes-sidebar-dropdown'
            groupedLocations={[Location.APPS, Location.APIS, Location.WEBHOOKS]}
          >
            {renderSidebarDropdownItem(Location.APPS)}
            {renderSidebarDropdownItem(Location.APIS)}
            {renderSidebarDropdownItem(Location.WEBHOOKS)}
          </SidebarItemDropdown>
        )}
      </SidebarGroup>
    </div>
  );

  return (
    <div className={styles.sidebar(props.open)}>
      <div className={styles.menu}>
        <div className={styles.sidebarToggle}>
          {props.open ? (
            <>
              <MenuItem type={MenuItemType.LIGHT}>Painel</MenuItem>
              <DrawerIcon onClick={props.onToggleClick} />
            </>
          ) : (
            <ChevronIcon onClick={props.onToggleClick} />
          )}
        </div>
        <SidebarUser
          sidebarOpen={props.open}
          currentLocation={currentLocation}
          changeUserFleet={props.changeUserFleet}
        />
        <SidebarOnboarding sidebarOpen={props.open} />
        {renderSidebarEntries()}
      </div>

      <SidebarFooter open={props.open} logout={props.logout} />
    </div>
  );
};
